import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from './AuthContext';
import { supabase } from '../lib/supabase';
import type { UserContextData, UserBelong, UserRole } from '../types/user';
// stripped debug

const UserContext = createContext<UserContextData | undefined>(undefined);

// Fjernet ubrukt User-interface



export function UserProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [residences, setResidences] = useState<UserBelong[]>([]);
  const [currentResidence, setCurrentResidence] = useState<UserBelong | null>(() => {
    try {
      const saved = localStorage.getItem('currentResidence');
      if (!saved) return null;
      const parsed = JSON.parse(saved);
      return parsed ?? null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const queryClient = useQueryClient();
  const [membershipLoading, setMembershipLoading] = useState<boolean>(true);
  const [membershipResolved, setMembershipResolved] = useState<boolean>(false);

  // Flyttede sammenligningshjelpere utenfor render-scope for å stabilisere referanser
  const isSameBelong = useCallback((a: UserBelong | null, b: UserBelong | null): boolean => {
    if (!a && !b) return true;
    if (!a || !b) return false;
    return (
      a.id === b.id &&
      a.profiles_id === b.profiles_id &&
      a.residence_id === b.residence_id &&
      a.apartment_id === b.apartment_id &&
      a.role === b.role
    );
  }, []);

  const areSameBelongsArray = useCallback((a: UserBelong[], b: UserBelong[]): boolean => {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!isSameBelong(a[i], b[i])) return false;
    }
    return true;
  }, [isSameBelong]);

  // no global debug exposure

  const fetchUserData = useCallback(async () => {
    if (!user?.id) {
      setResidences([]);
      setCurrentResidence(null);
      setLoading(false);
      // membership er irrelevant når ingen bruker finnes
      setMembershipLoading(false);
      setMembershipResolved(true);
      return;
    }

    // No time-based cache in stripped version

    let timeoutId: number | undefined;
    
    try {
      setLoading(true);
      setError(null);

      // Timeout-mekanisme for å unngå evig loading
      timeoutId = window.setTimeout(() => {
        console.warn('[UserContext] fetchUserData timeout (10s) - forcing completion');
        // Sett begge loading-states til false for å unngå evig loading
        setLoading(false);
        setMembershipLoading(false);
        setMembershipResolved(true);
      }, 10000); // 10 sekunder timeout

      // 1) Rask boolean-sjekk - bruk cached data først, deretter rask count-query
      setMembershipLoading(true);
      let hasResidences = false;
      
      // Sjekk cache først for umiddelbar respons
      const cachedBelongs = queryClient.getQueryData<any[]>(['user', user.id, 'belongs']);
      if (cachedBelongs && cachedBelongs.length > 0) {
        console.log('[UserContext] Using cached residences:', cachedBelongs.length);
        hasResidences = true;
      } else {
        // Rask count-query uten retry (raskere enn RPC) med 3 sekunder timeout
        try {
          console.log('[UserContext] Checking for residences via count query...');
          const countStartTime = Date.now();
          const { count, error: countError } = await Promise.race([
            supabase
              .from('user_belong')
              .select('id', { head: true, count: 'exact' })
              .eq('profiles_id', user.id),
            new Promise<{ count: null; error: { message: string } }>((_, reject) => 
              setTimeout(() => reject({ count: null, error: { message: 'Timeout' } }), 3000)
            )
          ]) as any;
          
          const countDuration = Date.now() - countStartTime;
          console.log('[UserContext] Count query completed:', { count, countError, durationMs: countDuration });
          
          if (countError) {
            console.warn('[UserContext] Count query failed:', countError);
            hasResidences = false;
          } else {
            hasResidences = (count ?? 0) > 0;
            console.log('[UserContext] Has residences:', hasResidences);
          }
        } catch (error) {
          console.warn('[UserContext] Count query timeout or error, assuming no residences:', error);
          hasResidences = false;
        }
      }
      // 2) Hent tilhørigheter - bruk cache først, deretter database med timeout
      // Hvis vi har cached data, bruk den umiddelbart
      if (cachedBelongs && cachedBelongs.length > 0 && residences.length === 0) {
        try {
          const mappedCached = cachedBelongs.map((item: any) => ({
            id: Number(item.id),
            profiles_id: item.profiles_id,
            residence_id: Number(item.residence_id),
            apartment_id: item.apartment_id ? Number(item.apartment_id) : null,
            role: item.role,
            is_admin: item.is_admin || false,
            residence: {
              name: item.residence?.name || '',
              type: item.residence?.type
            },
            apartment: item.apartment?.number ? {
              number: item.apartment.number
            } : undefined
          } as UserBelong));
          setResidences(mappedCached);
          if (!currentResidence && mappedCached.length > 0) {
            setCurrentResidence(mappedCached[0]);
          }
          // Hvis vi har cached data, kan vi returnere tidlig og oppdatere i bakgrunnen
          setMembershipLoading(false);
          setMembershipResolved(true);
          clearTimeout(timeoutId);
        } catch {
          // ignorer cache-mapping feil, fortsett til database-henting
        }
      }

      // Hent fra database med timeout (kun hvis vi ikke har cached data eller den er tom)
      if (!cachedBelongs || cachedBelongs.length === 0) {
        try {
          console.log('[UserContext] Fetching residences from user_data_view...');
          const fetchStartTime = Date.now();
          const { data, error: fetchError } = await Promise.race([
            supabase
              .from('user_data_view')
              .select('*')
              .eq('profiles_id', user.id),
            new Promise<{ data: null; error: { message: string } }>((_, reject) => 
              setTimeout(() => reject({ data: null, error: { message: 'Timeout' } }), 5000)
            )
          ]) as any;

          const fetchDuration = Date.now() - fetchStartTime;
          console.log('[UserContext] user_data_view query completed:', { 
            dataLength: data?.length, 
            fetchError, 
            durationMs: fetchDuration 
          });

          if (fetchError) {
            console.warn('[UserContext] user_data_view query failed:', fetchError);
            throw fetchError;
          }

          if (data && data.length > 0) {
            console.log('[UserContext] Successfully fetched residences:', data.length);
            const mappedResidences = data.map(item => ({
              id: Number(item.user_belong_id),
              profiles_id: item.profiles_id,
              residence_id: Number(item.residence_id),
              apartment_id: item.apartment_id ? Number(item.apartment_id) : null,
              role: item.role,
              is_admin: item.is_admin || false,
              residence: {
                name: item.residence_name || '',
                type: item.residence_type
              },
              apartment: item.apartment_number ? {
                number: item.apartment_number
              } : undefined
            } as UserBelong));

            // Cache resultatet for fremtidig bruk
            queryClient.setQueryData(['user', user.id, 'belongs'], mappedResidences);

            // Bare oppdater hvis innhold faktisk endres
            if (!areSameBelongsArray(residences, mappedResidences)) {
              setResidences(mappedResidences);
            }
            
            // Sett første leilighet som aktiv hvis ingen er valgt
            if (!currentResidence && mappedResidences.length > 0) {
              setCurrentResidence(mappedResidences[0]);
            } else if (currentResidence) {
              // Bevar referanse hvis samme id finnes
              const same = mappedResidences.find(r => r.id === currentResidence.id);
              if (same && !isSameBelong(currentResidence, same)) {
                setCurrentResidence(same);
              }
            }
            
            setMembershipLoading(false);
            setMembershipResolved(true);
          } else {
            console.log('[UserContext] No residences found in user_data_view');
            setResidences([]);
            setCurrentResidence(null);
            setMembershipLoading(false);
            setMembershipResolved(true);
          }
        } catch (error) {
          console.warn('[UserContext] user_data_view query timeout or error:', error);
          // Ved timeout eller feil, bruk cached data hvis tilgjengelig
          if (cachedBelongs && cachedBelongs.length > 0) {
            // Cache er allerede brukt over, bare fortsett
          } else {
            setResidences([]);
            setCurrentResidence(null);
          }
        }
      }

      setMembershipLoading(false);
      setMembershipResolved(true);
      clearTimeout(timeoutId); // Rydd opp timeout
    } catch {
      setError(new Error('Kunne ikke hente brukerdata'));
    } finally {
      setLoading(false);
      setMembershipLoading(false);
      setMembershipResolved(true);
      clearTimeout(timeoutId); // Rydd opp timeout
    }
  }, [user?.id, queryClient, areSameBelongsArray, isSameBelong, residences, currentResidence]);

  // Synkroniser med AuthContext sin user state - enkel tilnærming
  // Viktig: avheng kun av user?.id for å unngå re-render loop via funksjonsreferanser
  useEffect(() => {
    let hardTimeoutTimer: number | undefined;
    
    if (user?.id) {
      setMembershipLoading(true);
      setMembershipResolved(false);
      
      // HARD TIMEOUT: Sikrer at membershipLoading aldri henger mer enn 8 sekunder
      hardTimeoutTimer = window.setTimeout(() => {
        console.warn('[UserContext] HARD TIMEOUT reached (8s) - forcing membershipLoading = false');
        setMembershipLoading(false);
        setMembershipResolved(true);
        setLoading(false);
      }, 8000); // 8 sekunder hard timeout
      
      // Kjør henting uten å ha fetchUserData i deps for å unngå ustabil referanse
      void (async () => {
        try {
          await fetchUserData();
        } catch (error) {
          console.error('[UserContext] fetchUserData failed in useEffect:', error);
          // Sørg for at loading-states settes til false ved feil
          setMembershipLoading(false);
          setMembershipResolved(true);
          setLoading(false);
        }
      })();
    } else {
      // Nullstill state når bruker forsvinner eller byttes
      setResidences([]);
      setCurrentResidence(null);
      setError(null);
      setLoading(false);
      setMembershipLoading(false);
      setMembershipResolved(true);
    }
    
    return () => {
      if (hardTimeoutTimer) {
        clearTimeout(hardTimeoutTimer);
      }
    };
  }, [user?.id]); // Ikke inkluder fetchUserData i deps for å unngå re-render loop 

  // Lagre currentResidence i localStorage og invalidere cache ved endring
  useEffect(() => {
    if (currentResidence) {
      try {
        const existing = localStorage.getItem('currentResidence');
        const next = JSON.stringify(currentResidence);
        if (existing !== next) {
          localStorage.setItem('currentResidence', next);
          
          // Invalider React Query cache for det nye residenceId for å hente oppdatert data
          const newResidenceId = currentResidence.residence_id;
          void queryClient.invalidateQueries({ 
            queryKey: ['residence', newResidenceId] 
          });
        }
      } catch {
        // ignorer
      }
    }
  }, [currentResidence, queryClient]);

  // Hent lagret currentResidence fra localStorage ved oppstart (per bruker)
  useEffect(() => {
    if (!user?.id) {
      localStorage.removeItem('currentResidence');
      return;
    }
    const savedResidence = localStorage.getItem('currentResidence');
    if (savedResidence) {
      try {
        const parsed = JSON.parse(savedResidence);
        // Verifiser at den lagrede residence tilhører gjeldende bruker og fortsatt er gyldig
        const isSameUser = parsed?.profiles_id === user.id;
        const isValidForUser = residences.some(r => r.residence_id === parsed.residence_id && r.profiles_id === user.id);
        if (isSameUser && isValidForUser) {
          if (!isSameBelong(currentResidence, parsed)) {
            setCurrentResidence(parsed);
          }
        } else {
          localStorage.removeItem('currentResidence');
        }
      } catch {
        localStorage.removeItem('currentResidence');
      }
    }
  }, [user?.id, residences, currentResidence, isSameBelong]);

  // refreshUserData var ubrukt; bruk fetchUserData direkte i value

  const value: UserContextData = {
    id: user?.id || '',
    residences,
    currentResidence,
    setCurrentResidence: (residence: UserBelong) => setCurrentResidence(residence),
    loading,
    error,
    refreshUserData: fetchUserData,
    hasAccess: (requiredRole: UserRole) => {
      if (user?.isAdmin) return true;
      return currentResidence?.role === requiredRole;
    },
    isAdmin: user?.isAdmin || false,
    hasResidence: residences.length > 0,
    membershipLoading,
    membershipResolved
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
} 